import type {
    IResponseTiming,
    IResponseTimingInput,
} from "@agrigate/survey-api-def/lib/SurveysService/dto";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import { useCallback, useEffect, useRef } from "react";

const MAX_SEGMENT_MS = 5 * 60_000;

interface QuestionTimingAccum {
    activeMs: number;
    visits: number;
}

export default function useQuestionTiming(
    currentQuestionId: MaybeN<string>,
    initialTiming?: MaybeN<IResponseTiming>,
): { readTiming: () => IResponseTimingInput } {
    const timingRef = useRef<Record<string, QuestionTimingAccum>>({});
    const segmentRef = useRef<MaybeN<{ qId: string; startedAt: number }>>(null);

    const commitSegment = useCallback((pause = false) => {
        const segment = segmentRef.current;
        if (!segment) return;
        const elapsed = Math.min(
            Math.max(0, performance.now() - segment.startedAt),
            MAX_SEGMENT_MS,
        );
        const current = timingRef.current[segment.qId] ?? {
            activeMs: 0,
            visits: 0,
        };
        timingRef.current[segment.qId] = {
            ...current,
            activeMs: current.activeMs + elapsed,
        };
        segmentRef.current = pause
            ? null
            : { qId: segment.qId, startedAt: performance.now() };
    }, []);

    useEffect(() => {
        for (const [qId, stored] of Object.entries(
            initialTiming?.byQuestion ?? {},
        )) {
            const current = timingRef.current[qId] ?? {
                activeMs: 0,
                visits: 0,
            };
            timingRef.current[qId] = {
                activeMs: Math.max(current.activeMs, stored?.activeMs ?? 0),
                visits: Math.max(current.visits, stored?.visits ?? 0),
            };
        }
    }, [initialTiming]);

    useEffect(() => {
        if (!currentQuestionId) return;
        const current = timingRef.current[currentQuestionId] ?? {
            activeMs: 0,
            visits: 0,
        };
        timingRef.current[currentQuestionId] = {
            ...current,
            visits: current.visits + 1,
        };
        segmentRef.current = {
            qId: currentQuestionId,
            startedAt: performance.now(),
        };
        return () => commitSegment(true);
    }, [currentQuestionId, commitSegment]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                commitSegment(true);
            } else if (currentQuestionId && !segmentRef.current) {
                segmentRef.current = {
                    qId: currentQuestionId,
                    startedAt: performance.now(),
                };
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () =>
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange,
            );
    }, [currentQuestionId, commitSegment]);

    const readTiming = useCallback((): IResponseTimingInput => {
        commitSegment();
        const byQuestion: IResponseTimingInput["byQuestion"] = {};
        for (const [qId, accum] of Object.entries(timingRef.current)) {
            byQuestion[qId] = {
                activeMs: Math.round(accum.activeMs),
                visits: accum.visits,
            };
        }
        return { byQuestion, currentQuestionId };
    }, [commitSegment, currentQuestionId]);

    return { readTiming };
}
